import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = "atlas_token";
export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setToken = (t) => {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch {}
};

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const searchVehicles = async (criteria) => {
  const { data } = await client.post("/search", criteria);
  return data;
};

export const getAllVehicles = async () => {
  const { data } = await client.get("/vehicles");
  return data;
};

export const getVehicle = async (id) => {
  const { data } = await client.get(`/vehicles/${id}`);
  return data;
};

// --- Phase 2A/2B: genuine AtlasVehicles + Comparable Engine ---
export const getAtlasVehicles = async () => {
  const { data } = await client.get("/atlas/vehicles");
  return data;
};

export const getAtlasVehicle = async (source, id) => {
  const { data } = await client.get(`/atlas/vehicles/${source}/${id}`);
  return data;
};

export const getAtlasComparables = async (source, id) => {
  const { data } = await client.get(`/atlas/comparables/${source}/${id}`);
  return data;
};

export const searchAtlas = async (params) => {
  const { data } = await client.get("/atlas/search", { params });
  return data;
};

export const getAtlasLiveVehicle = async (source, id) => {
  const { data } = await client.get(`/atlas/live/${source}/${id}`);
  return data;
};

export const compareAtlasVehicles = async (vehicles) => {
  const { data } = await client.post("/atlas/compare", { vehicles });
  return data;
};

export const getFx = async () => {
  const { data } = await client.get("/atlas/fx");
  return data;
};

export const getAtlasMarkets = async () => {
  const { data } = await client.get("/atlas/markets");
  return data;
};

export const verifyAvailability = async (items) => {
  const { data } = await client.post("/atlas/verify-availability", { items });
  return data;
};

export const compareVehicles = async (vehicleIds, criteria = null) => {
  const { data } = await client.post("/compare", { vehicle_ids: vehicleIds, criteria });
  return data;
};

export const getMarketInsights = async () => {
  const { data } = await client.get("/market-insights");
  return data;
};

export const getMakes = async () => {
  const { data } = await client.get("/makes");
  return data;
};

export const getMarkets = async () => {
  const { data } = await client.get("/markets");
  return data;
};

export const getSourceMeta = async () => {
  const { data } = await client.get("/source-submissions/meta");
  return data;
};

export const submitSource = async (payload) => {
  const { data } = await client.post("/source-submissions", payload);
  return data;
};

export const registerUser = async (payload) => {
  const { data } = await client.post("/auth/register", payload);
  return data;
};

export const loginUser = async (payload) => {
  const { data } = await client.post("/auth/login", payload);
  return data;
};

export const getMe = async () => {
  const { data } = await client.get("/auth/me");
  return data;
};

// Emergent-managed Google sign-in
export const exchangeGoogleSession = async (sessionId) => {
  const { data } = await client.post("/auth/google/session", null, {
    headers: { "X-Session-ID": sessionId },
  });
  return data;
};

export const logoutApi = async () => {
  try { await client.post("/auth/logout", {}); } catch { /* ignore */ }
};

export const verifyEmail = async (token) => {
  const { data } = await client.post("/auth/verify-email", { token });
  return data;
};

export const resendVerification = async (email) => {
  const { data } = await client.post("/auth/resend-verification", { email });
  return data;
};

export const forgotPassword = async (email) => {
  const { data } = await client.post("/auth/forgot-password", { email });
  return data;
};

export const resetPassword = async (token, new_password) => {
  const { data } = await client.post("/auth/reset-password", { token, new_password });
  return data;
};

export const getAccount = async () => {
  const { data } = await client.get("/auth/account");
  return data;
};

export const acceptCurrentTerms = async () => {
  const { data } = await client.post("/auth/accept-current", {});
  return data;
};

export const getLegalMeta = async () => {
  const { data } = await client.get("/legal/meta");
  return data;
};

export const getReportMeta = async () => {
  const { data } = await client.get("/reports/meta");
  return data;
};

export const createReport = async (payload) => {
  const { data } = await client.post("/reports", payload);
  return data;
};

// --- Atlas Intelligence (B2B acquisition) ---
export const getOpportunityMeta = async () => (await client.get("/opportunities/meta")).data;
export const previewOpportunity = async (payload) => (await client.post("/opportunities/preview", payload)).data;
export const createOpportunity = async (payload) => (await client.post("/opportunities", payload)).data;
export const listOpportunities = async () => (await client.get("/opportunities")).data;
export const getOpportunity = async (id) => (await client.get(`/opportunities/${id}`)).data;
export const deleteOpportunity = async (id) => (await client.delete(`/opportunities/${id}`)).data;
export const getDealerProfile = async () => (await client.get("/dealer-profile")).data;
export const saveDealerProfile = async (payload) => (await client.put("/dealer-profile", payload)).data;
export const updateProfile = async (name) => (await client.patch("/auth/profile", { name })).data;
export const shareOpportunity = async (id) => (await client.post(`/opportunities/${id}/share`)).data;
export const unshareOpportunity = async (id) => (await client.delete(`/opportunities/${id}/share`)).data;
export const getPublicOpportunity = async (token) => (await client.get(`/public/opportunities/${token}`)).data;

